import { PhoneOff } from 'lucide-react';

/**
 * Horizontal status dock rendered directly below the header when voice call
 * mode is active. Shows a pulsing mic-style indicator + state label + hang-up
 * button. The caller owns the decision of when to render this — VoiceDock
 * itself never decides visibility.
 *
 * @param {{ state: { state: string, label: string }, onHangUp: () => void }} props
 */
export default function VoiceDock({ state, onHangUp }) {
  const { state: mode, label } = state;
  const isActive = mode === 'listening' || mode === 'listening-answer' || mode === 'arming'
    || mode === 'transcribing' || mode === 'correcting';
  const isError = mode === 'error';
  let dotClass = 'bg-secondary-400'; // idle
  if (isError) dotClass = 'bg-error-500';
  else if (isActive) dotClass = 'bg-primary-500';

  return (
    <div className={`flex items-center gap-3 px-4 py-2 border-b border-[var(--app-border)] ${isError ? 'bg-error-50/70 dark:bg-error-950/30' : 'bg-primary-50/60 dark:bg-primary-950/30'}`}>
      {/* Pulsing indicator — red and static when the loop stopped on an error */}
      <span className="relative flex h-3 w-3 shrink-0">
        {isActive && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />}
        <span className={`relative inline-flex h-3 w-3 rounded-full ${dotClass}`} />
      </span>

      {/* State label — an error explains itself rather than silently stopping */}
      <span className={`flex-1 text-sm truncate ${isError ? 'text-error-700 dark:text-error-300' : 'text-primary-700 dark:text-primary-300'}`}>
        {label}
      </span>

      {/* Last utterance chip (shown briefly after a turn is dispatched) */}
      {mode === 'sent' && (
        <span className="text-xs text-secondary-500 italic truncate max-w-[200px]">
          &ldquo;{label}&rdquo;
        </span>
      )}

      {/* Hang up button */}
      <button
        type="button"
        onClick={onHangUp}
        title="End voice mode"
        aria-label="End voice mode"
        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40 shrink-0 transition-colors"
      >
        <PhoneOff size={15} />
      </button>
    </div>
  );
}
